import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useGetApprovalQuery } from "components/approval-review-table/endpoints";
import {
  setSelectedApproval,
  setHasApprovalLoadingError,
  setIsApprovalLoading,
  setApprovalError,
} from "features/approval/slice";

interface UseApprovalReviewLoadParams {
  parentId: string;
  teamName: string;
}

/**
 * Hook to load approval review data and dispatch it to Redux store.
 * This hook should be called at the page level to ensure approval data
 * is loaded regardless of which tab is active.
 */
export const useApprovalReviewLoader = ({
  parentId,
  teamName,
}: UseApprovalReviewLoadParams) => {
  const dispatch = useDispatch();

  const { data, isLoading, isError, error } = useGetApprovalQuery(
    { parentId, teamName },
    {
      refetchOnMountOrArgChange: true,
      skip: !parentId || parentId === "" || !teamName || teamName === "",
    },
  );

  useEffect(() => {
    dispatch(setIsApprovalLoading(isLoading));
  }, [isLoading, dispatch]);

  useEffect(() => {
    if (!isLoading) {
      if (data?.approval) {
        dispatch(setSelectedApproval(data.approval));
      } else {
        dispatch(setSelectedApproval(null));
      }

      if (isError) {
        dispatch(setHasApprovalLoadingError(isError));
        dispatch(setApprovalError(error));
      } else {
        dispatch(setApprovalError(null));
      }
    }
  }, [data, isLoading, dispatch, isError, error]);

  return {
    data,
    isLoading,
    isError,
    error,
  };
};
